import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions } from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  constructor() {
    // Expose Swal globally so inline onclick="Swal.close()" in custom HTML works
    (window as any)['Swal'] = Swal;
  }

  /**
   * แสดง popup แจ้งเตือนทั่วไป (Custom UI)
   */
  alert(title: string, message: string, icon: 'success' | 'error' | 'warning' | 'info' | 'question' = 'info') {
    return Swal.fire({
      html: this.buildCustomHTML(title, message, icon),
      showCancelButton: false,
      confirmButtonText: 'ตกลง',
      buttonsStyling: false,
      customClass: this.getCustomClasses(icon),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup ยืนยันการทำงาน (Confirm) (Custom UI)
   */
  confirm(title: string, message: string, confirmText: string = 'ยืนยัน', cancelText: string = 'ยกเลิก') {
    return Swal.fire({
      html: this.buildCustomHTML(title, message, 'question'),
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      buttonsStyling: false,
      customClass: this.getCustomClasses('question'),
      backdrop: 'rgba(15, 23, 42, 0.3)',
      reverseButtons: true
    });
  }

  /**
   * แสดง popup แจ้งเตือนว่า Token หมดอายุ (Custom UI)
   */
  showTokenExpiredDialog(): Promise<void> {
    return Swal.fire({
      html: this.buildCustomHTML('เซสชันหมดอายุ', 'เซสชันการเข้าสู่ระบบของคุณหมดอายุแล้ว กรุณาเข้าสู่ระบบใหม่อีกครั้ง', 'warning'),
      confirmButtonText: 'เข้าสู่ระบบใหม่',
      buttonsStyling: false,
      customClass: this.getCustomClasses('warning'),
      backdrop: 'rgba(15, 23, 42, 0.3)',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: true
    }).then(() => {
      // สำเร็จเมื่อกด OK หรือปิด popup
      return;
    });
  }

  /**
   * แสดง popup สำเร็จ (Custom UI)
   */
  success(title: string, message: string = '', confirmText: string = 'ตกลง') {
    return Swal.fire({
      html: this.buildCustomHTML(title, message, 'success'),
      confirmButtonText: confirmText,
      buttonsStyling: false,
      customClass: this.getCustomClasses('success'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup ข้อผิดพลาด (Custom UI)
   */
  error(title: string, message: string = '', confirmText: string = 'ตกลง') {
    return Swal.fire({
      html: this.buildCustomHTML(title, message, 'error'),
      confirmButtonText: confirmText,
      buttonsStyling: false,
      customClass: this.getCustomClasses('error'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup แจ้งเตือน (Custom UI)
   */
  warning(title: string, message: string = '', confirmText: string = 'ตกลง') {
    return Swal.fire({
      html: this.buildCustomHTML(title, message, 'warning'),
      confirmButtonText: confirmText,
      buttonsStyling: false,
      customClass: this.getCustomClasses('warning'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup ข้อมูล (Custom UI)
   */
  info(title: string, message: string = '', confirmText: string = 'ตกลง') {
    return Swal.fire({
      html: this.buildCustomHTML(title, message, 'info'),
      confirmButtonText: confirmText,
      buttonsStyling: false,
      customClass: this.getCustomClasses('info'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup ยืนยันการลบ (Custom UI)
   */
  confirmDelete(itemName: string, confirmText: string = 'ลบ', cancelText: string = 'ยกเลิก') {
    return Swal.fire({
      html: this.buildCustomHTML('ยืนยันการลบ', `คุณต้องการลบ "${itemName}" หรือไม่?`, 'delete'),
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      buttonsStyling: false,
      customClass: this.getCustomClasses('delete'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup สำเร็จแบบสั้น (มี timer)
   */
  successToast(message: string, timer: number = 1500) {
    return Swal.fire({
      html: this.buildCustomHTML('สำเร็จ!', message, 'success'),
      timer: timer,
      showConfirmButton: false,
      buttonsStyling: false,
      customClass: this.getCustomClasses('success'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup แจ้งเตือนแบบสั้น (มี timer)
   */
  warningToast(message: string, timer: number = 2000) {
    return Swal.fire({
      html: this.buildCustomHTML('เตือน!', message, 'warning'),
      timer: timer,
      showConfirmButton: false,
      buttonsStyling: false,
      customClass: this.getCustomClasses('warning'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup ข้อมูลแบบสั้น (มี timer)
   */
  infoToast(message: string, timer: number = 2000) {
    return Swal.fire({
      html: this.buildCustomHTML('ข้อมูล', message, 'info'),
      timer: timer,
      showConfirmButton: false,
      buttonsStyling: false,
      customClass: this.getCustomClasses('info'),
      backdrop: 'rgba(15, 23, 42, 0.3)'
    });
  }

  /**
   * แสดง popup loading
   */
  loading(title: string = 'กำลังดำเนินการ...', message: string = 'กรุณารอสักครู่') {
    return Swal.fire({
      title,
      text: message,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  /**
   * ปิด popup ที่เปิดอยู่
   */
  close() {
    Swal.close();
  }

  /**
   * แสดง Toast notification
   */
  toast(message: string, icon: 'success' | 'error' | 'warning' | 'info' = 'success') {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });

    return Toast.fire({
      icon,
      title: message
    });
  }

  // ========== Private Helper Methods ==========

  /**
   * สร้าง Custom HTML สำหรับ dialog
   */
  private buildCustomHTML(title: string, message: string, type: string): string {
    const iconConfig = this.getIconConfig(type);
    
    return `
      <style>@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap');</style>
      <div style="font-family:'Sarabun',sans-serif;padding:2.5rem;position:relative">
        <button onclick="Swal.close()" style="position:absolute;top:1.5rem;right:1.5rem;padding:0.5rem;color:#cbd5e1;background:transparent;border:none;border-radius:9999px;cursor:pointer;transition:all 0.2s" onmouseover="this.style.color='#64748b';this.style.backgroundColor='#f8fafc'" onmouseout="this.style.color='#cbd5e1';this.style.backgroundColor='transparent'">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <div style="display:flex;flex-direction:column;align-items:center;text-align:center">
          <div style="width:5rem;height:5rem;background:${iconConfig.bgGradient};border-radius:1.75rem;display:flex;align-items:center;justify-content:center;margin-bottom:2rem;position:relative">
            <div style="position:absolute;inset:0;background:${iconConfig.glowGradient};opacity:0.5;filter:blur(40px);border-radius:9999px"></div>
            ${iconConfig.svg}
          </div>
          <h2 style="font-size:1.625rem;font-weight:700;color:#1e293b;margin-bottom:0.75rem;letter-spacing:-0.025em">${title}</h2>
          <p style="color:#64748b;font-size:1.0625rem;margin-bottom:2.5rem;line-height:1.6;font-weight:300">${message}</p>
        </div>
      </div>
    `;
  }

  /**
   * ดึง icon config ตาม type
   */
  private getIconConfig(type: string) {
    const configs: Record<string, any> = {
      success: {
        bgGradient: 'linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%)',
        glowGradient: 'linear-gradient(135deg,#bbf7d0 0%,#86efac 100%)',
        svg: '<svg style="position:relative;z-index:10" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
      },
      error: {
        bgGradient: 'linear-gradient(135deg,#fef2f2 0%,#fee2e2 100%)',
        glowGradient: 'linear-gradient(135deg,#fecaca 0%,#fca5a5 100%)',
        svg: '<svg style="position:relative;z-index:10" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
      },
      warning: {
        bgGradient: 'linear-gradient(135deg,#fffbeb 0%,#fef3c7 100%)',
        glowGradient: 'linear-gradient(135deg,#fde68a 0%,#fcd34d 100%)',
        svg: '<svg style="position:relative;z-index:10" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>'
      },
      question: {
        bgGradient: 'linear-gradient(135deg,#eff6ff 0%,#dbeafe 100%)',
        glowGradient: 'linear-gradient(135deg,#bfdbfe 0%,#93c5fd 100%)',
        svg: '<svg style="position:relative;z-index:10" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>'
      },
      delete: {
        bgGradient: 'linear-gradient(135deg,#fef2f2 0%,#fee2e2 100%)',
        glowGradient: 'linear-gradient(135deg,#fecaca 0%,#fca5a5 100%)',
        svg: '<svg style="position:relative;z-index:10" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>'
      },
      info: {
        bgGradient: 'linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 100%)',
        glowGradient: 'linear-gradient(135deg,#bae6fd 0%,#7dd3fc 100%)',
        svg: '<svg style="position:relative;z-index:10" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
      }
    };
    return configs[type] || configs['info'];
  }

  /**
   * ดึง custom CSS classes ตาม type
   */
  private getCustomClasses(type: string) {
    const colorMap: Record<string, any> = {
      success: { confirm: 'bg-green-500 hover:bg-green-600 shadow-green-200', cancel: 'bg-slate-100 hover:bg-slate-200' },
      error: { confirm: 'bg-red-500 hover:bg-red-600 shadow-red-200', cancel: 'bg-slate-100 hover:bg-slate-200' },
      warning: { confirm: 'bg-amber-500 hover:bg-amber-600 shadow-amber-200', cancel: 'bg-slate-100 hover:bg-slate-200' },
      question: { confirm: 'bg-blue-500 hover:bg-blue-600 shadow-blue-200', cancel: 'bg-slate-100 hover:bg-slate-200' },
      delete: { confirm: 'bg-red-500 hover:bg-red-600 shadow-red-200', cancel: 'bg-slate-100 hover:bg-slate-200' },
      info: { confirm: 'bg-sky-500 hover:bg-sky-600 shadow-sky-200', cancel: 'bg-slate-100 hover:bg-slate-200' }
    };

    const colors = colorMap[type] || colorMap['question'];

    return {
      popup: 'rounded-[2.5rem] shadow-2xl border border-white/50',
      htmlContainer: 'p-0 m-0',
      actions: 'w-full flex gap-4 px-10 pb-10',
      confirmButton: `flex-1 py-4 ${colors.confirm} text-white rounded-[1.375rem] font-bold text-lg transition-all shadow-lg`,
      cancelButton: `flex-1 py-4 ${colors.cancel} text-slate-600 rounded-[1.375rem] font-semibold text-lg transition-all`
    };
  }
}
